import React, { useContext, useEffect, useRef, useState } from "react";
import { AttributeContext } from "../AttributeContext";
import { getConstraintsByAttributeID } from "api/Attributes";
import { toast } from 'Components/DesignSystem/Toast/useToast'
import Accordion from "Components/DesignSystem/Accordion/Accordion";
import Alert from "Components/DesignSystem/Alert/Alert";
import Typography from "Components/DesignSystem/Typography/Typography";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";

const ConstraintsItem = ({ label, value, col = 12 }) => {
  return (
    <div className="col-6">
      <Typography variant="body-primary" as="p" className="tw-mb-0 tw-text-ink">
        {`${label}:`}
      </Typography>
      <Typography variant="body-primary" as="p" className="tw-mb-0 tw-text-ink-secondary">
        {`${value}`}
      </Typography>
    </div>
  );
};

/**
 * Read-only "Attribute Constraints" accordion. Fetch logic is verbatim (lazy
 * fetch on first expand, keyed by attrId ref, incl. the duplicate-fetch guard
 * living inside fetchAttributes); only the rendering moved onto DS
 * Accordion/Alert/Typography/EmptyState — the `#A1A1A1` value ink is now
 * `tw-text-ink-secondary`.
 */
const AttributeConstraintView = () => {
  const attributeContextDate = useContext(AttributeContext);
  const [loading, setLoading] = useState({
    status: "",
    message: "",
  });
  const [collapse, setCollapse] = useState(false);
  const [enableEdit, setEnableEdit] = useState(false);
  const attrId = useRef(null)


  const [attributeConstraintView, setAttributeConstraintView] = useState(null);

  useEffect(() => {
    setLoading({
      status: "",
      message: "",
    });
  }, [attributeContextDate.editingItem.id]);

  useEffect(() => {
    if (collapse) {
      fetchAttributes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapse]);

  async function fetchAttributes() {
    if(!collapse || attrId.current === attributeContextDate.editingItem.id) return
    attrId.current = attributeContextDate.editingItem.id
    setLoading({status: "Loading", message: "Loading..."})
    getConstraintsByAttributeID(attributeContextDate.editingItem.id)
    .then((res) => {
      setAttributeConstraintView(res)
      setLoading({
        status: "Success",
        message: ""
      })
    })
    .catch(err => {
      setLoading({
        status: "Error",
        message: err.message
      })
      toast.error(err.message)
    })
  }

  function handle_collapse() {
    setCollapse(!collapse);
  }

  return (
    <Accordion
      title={`${enableEdit ? "Update " : ""} Attribute Constraints`}
      open={collapse}
      onOpenChange={() => handle_collapse()}
      data-testid="componets-attributeconstraintview-div-1"
      variant="boxed"
    >
      <div className="tw-pb-4">
      {/* Skeleton rows for the initial load — the module-wide loading grammar
          (user-requested 2026-09-01); the Alert strip remains for errors. */}
      {loading.status === "Loading" && (
        <div className="tw-space-y-3" data-testid="attribute-constraint-view-skeleton">
          <Skeleton variant="rect" width="40%" height="20px" />
          <Skeleton variant="rect" width="100%" height="20px" />
          <Skeleton variant="rect" width="100%" height="20px" />
          <Skeleton variant="rect" width="60%" height="20px" />
        </div>
      )}

      {loading.status === "Error" && (
        <Alert tone="danger" live data-testid="attribute-constraint-view-status">
          {loading.message}
        </Alert>
      )}

      {loading.status === "Success" && (
        <div className="p-3 tw-border tw-border-edge tw-rounded-ctl">
          {attributeConstraintView ? (
            <div className="container overflow-hidden">
              <div className="row gy-3">
                <ConstraintsItem
                  label="Validation"
                  value={`${attributeConstraintView.validation}`}
                />
                <ConstraintsItem
                  label="Allowed Chars"
                  value={attributeConstraintView.allowed_chars}
                />
                <ConstraintsItem
                  label="Attr id"
                  value={attributeConstraintView.id}
                />
                <ConstraintsItem
                  label="Allowed User Defined Values "
                  value={
                    attributeConstraintView.allowed_user_defined_values
                  }
                />
                <ConstraintsItem
                  label="Prohibited Chars"
                  value={`${attributeConstraintView.prohibited_chars}`}
                />
                <ConstraintsItem
                  label="Attr Inheritance"
                  value={`${attributeConstraintView.attr_inheritance}`}
                />
                <ConstraintsItem
                  label="Multi Select"
                  value={`${attributeConstraintView.multiselect}`}
                />

                <ConstraintsItem
                  label="Child Attr "
                  value={`${attributeConstraintView.child_attr}`}
                />

                <ConstraintsItem
                  label="Multiselect Number "
                  value={
                    attributeConstraintView.multiselect_number !==
                    null
                      ? attributeConstraintView.multiselect_number
                      : ""
                  }
                />

                <ConstraintsItem
                  label="Datatype "
                  value={attributeConstraintView.datatype}
                />

                <ConstraintsItem
                  label="Multiselect Output Range "
                  value={
                    attributeConstraintView.multiselect_output_range
                  }
                />

                <ConstraintsItem
                  label="Validation Type "
                  value={
                    attributeConstraintView.validation_type
                  }
                />

                <ConstraintsItem
                  label="Validation Type Extra Criteria "
                  value={
                    attributeConstraintView.validation_type_extra_criteria
                  }
                />

                <ConstraintsItem
                  label="Special Formatting "
                  value={
                    attributeConstraintView.special_formatting
                  }
                />

                <ConstraintsItem
                  label="Extra Criteria Value 1 "
                  value={
                    attributeConstraintView.extra_criteria_value1
                  }
                />

                <ConstraintsItem
                  label="Validation Range "
                  value={
                    attributeConstraintView.validation_range
                  }
                />

                <ConstraintsItem
                  label="Extra Criteria Value 2 "
                  value={`${attributeConstraintView.extra_criteria_value2}`}
                />

                <ConstraintsItem
                  label="Validation Error Alert Style "
                  value={
                    attributeConstraintView.validation_error_alert_title
                  }
                />

                <ConstraintsItem
                  label="Validation Input Message "
                  value={`${attributeConstraintView.validation_input_message}`}
                />

                <ConstraintsItem
                  label="Multiselect Output Range "
                  value={`${attributeConstraintView.multiselect_output_range}`}
                />

                <ConstraintsItem
                  label="Validation Error Alert "
                  value={`${attributeConstraintView.validation_error_alert}`}
                />
                <ConstraintsItem
                  label="Validation Error Alert Body "
                  value={`${attributeConstraintView.validation_error_alert_body}`}
                />
                <ConstraintsItem
                  label="Validation Error Alert Title "
                  value={`${attributeConstraintView.validation_error_alert_title}`}
                />
                <ConstraintsItem
                  label="Validation Input Message Body "
                  value={`${attributeConstraintView.validation_input_message_body}`}
                />
                <ConstraintsItem
                  label="Validation Input Message Title "
                  value={`${attributeConstraintView.validation_input_message_title}`}
                />
              </div>
            </div>
          ) : (
            <div className="tw-flex tw-justify-center tw-py-3">
              <EmptyState
                size="sm"
                title="No categories attributes constraints found"
                data-testid="attribute-constraint-view-empty"
              />
            </div>
          )}
        </div>
      )}
      </div>
    </Accordion>
  );
};

export default AttributeConstraintView;
